import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/roles/users')({
  component: UserAssignmentPage,
})

interface Role {
  id: string
  name: string
  isSystem: boolean
}

interface UserWithRoles {
  userId: string
  email: string
  name: string | null
  roleIds: string[]
}

function UserAssignmentPage() {
  const {
    data: usersData,
    isLoading: loadingUsers,
    refetch,
  } = useQuery(orpc.rbac.userRoles.listUsers.queryOptions({ input: {} }))
  const { data: rolesData } = useQuery(orpc.rbac.roles.list.queryOptions({ input: {} }))

  const users = (usersData ?? []) as UserWithRoles[]
  const roles = (rolesData ?? []) as Role[]

  const assignMutation = useMutation(
    orpc.rbac.userRoles.assign.mutationOptions({
      onSuccess: () => refetch(),
      onError: (e: Error) => toast.error(e.message),
    }),
  )
  const unassignMutation = useMutation(
    orpc.rbac.userRoles.unassign.mutationOptions({
      onSuccess: () => refetch(),
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const toggleRole = (user: UserWithRoles, roleId: string) => {
    if (user.roleIds.includes(roleId)) {
      unassignMutation.mutate({ userId: user.userId, roleId })
    } else {
      assignMutation.mutate({ userId: user.userId, roleId })
    }
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Users & Roles</h1>
        <p className="text-muted-foreground">Assign roles to users.</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {loadingUsers ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Roles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.userId}>
                    <TableCell>{u.name ?? '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm">
                            {u.roleIds.length === 0
                              ? 'No roles'
                              : u.roleIds
                                  .map((id) => roles.find((r) => r.id === id)?.name ?? id)
                                  .join(', ')}
                            <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-60" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[280px] p-0" align="start">
                          <div className="p-2 space-y-1">
                            {roles.map((role) => (
                              // biome-ignore lint/a11y/noLabelWithoutControl: Checkbox (Radix) is rendered inside but not detected by the rule
                              <label
                                key={role.id}
                                className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded cursor-pointer"
                              >
                                <Checkbox
                                  checked={u.roleIds.includes(role.id)}
                                  onCheckedChange={() => toggleRole(u, role.id)}
                                />
                                <span className="flex-1 text-sm">{role.name}</span>
                                {role.isSystem && (
                                  <Badge variant="outline" className="text-[10px]">
                                    sys
                                  </Badge>
                                )}
                              </label>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
